/**
 * Register Page - Neumorphic design with theme support
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../lib/api';
import { useTheme, Icons } from '../lib/design-system';
import Spinner from '../components/ui/Spinner';
import Collapsible from '../components/ui/Collapsible';
import { isNative } from '../lib/nativePhoto';
import {
  DOG_GENDER_OPTIONS,
  DOG_OWNERSHIP_OPTIONS,
  FIRST_DOG_OPTIONS,
  LIVING_SITUATION_OPTIONS,
  VACCINATION_STATUS_OPTIONS,
  firstDogFromForm,
} from '../lib/dogProfile';

export default function Register() {
  const { theme } = useTheme();
  const native = isNative();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // On iOS, trainer registration is not available (Apple 3.1.1/2.1(b): subscription
  // sign-up must not be initiated from the iOS app). Trainers must register at mojo.sschuldt.de.
  const [role, setRole] = useState<'TRAINER' | 'CUSTOMER'>('CUSTOMER');
  const [dogName, setDogName] = useState('');
  // Optional contact / intake fields (customer only, MOJ-30)
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [acquisitionSource, setAcquisitionSource] = useState<'' | 'referral' | 'web' | 'other'>('');
  // Optional dog-profile intake (MOJ-31) — customers only, all optional
  const [dogBreed, setDogBreed] = useState('');
  const [dogBirthDate, setDogBirthDate] = useState('');
  const [dogGender, setDogGender] = useState('');
  const [dogOwnership, setDogOwnership] = useState('');
  const [trainingSince, setTrainingSince] = useState('');
  const [isFirstDog, setIsFirstDog] = useState('');
  const [livingSituation, setLivingSituation] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [vaccinationStatus, setVaccinationStatus] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { setUser } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== passwordConfirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (!gdprConsent) {
      setError('Bitte stimme der Datenschutzerklärung und den Nutzungsbedingungen zu.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await authApi.register({
        email,
        password,
        firstName,
        lastName,
        role,
        dogName: role === 'CUSTOMER' ? dogName : undefined,
        gdprConsent: true,
        ...(role === 'CUSTOMER'
          ? {
              phone: phone.trim() || undefined,
              address: address.trim() || undefined,
              acquisitionSource: acquisitionSource || undefined,
              // Only send optional dog-profile fields the user actually filled in
              dogBreed: dogBreed || undefined,
              dogBirthDate: dogBirthDate || undefined,
              dogGender: dogGender || undefined,
              dogOwnership: dogOwnership || undefined,
              trainingSince: trainingSince || undefined,
              isFirstDog: firstDogFromForm(isFirstDog) ?? undefined,
              livingSituation: livingSituation || undefined,
              city: city || undefined,
              country: country || undefined,
              vaccinationStatus: vaccinationStatus || undefined,
            }
          : {}),
      });
      const { user, token } = response.data;
      setUser(user, token);
      navigate(role === 'TRAINER' ? '/trainer' : '/customer');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registrierung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 50%, ${theme.accent} 100%)`,
      }}
    >
      <div
        className="w-full max-w-md p-8 md:p-10 rounded-3xl shadow-2xl"
        style={{ backgroundColor: theme.surface }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div
            className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-neu-md"
            style={{ backgroundColor: theme.primaryLight }}
          >
            <Icons.Paw size={44} weight="duotone" style={{ color: theme.primary }} />
          </div>
          <h1
            className="text-3xl font-bold"
            style={{ color: theme.text }}
          >
            Mojo's Class
          </h1>
          <p
            className="text-sm mt-1"
            style={{ color: theme.textMuted }}
          >
            Konto erstellen
          </p>
        </div>

        {/* Register Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Role Toggle — trainer option hidden on iOS (Apple 3.1.1/2.1(b) compliance) */}
          {!native && (
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: theme.text }}
              >
                Ich bin
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setRole('CUSTOMER')}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all"
                  style={{
                    backgroundColor: role === 'CUSTOMER' ? theme.primary : theme.background,
                    color: role === 'CUSTOMER' ? '#fff' : theme.textMuted,
                  }}
                >
                  Hundebesitzer
                </button>
                <button
                  type="button"
                  onClick={() => setRole('TRAINER')}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all"
                  style={{
                    backgroundColor: role === 'TRAINER' ? theme.primary : theme.background,
                    color: role === 'TRAINER' ? '#fff' : theme.textMuted,
                  }}
                >
                  Hundetrainer
                </button>
              </div>
            </div>
          )}

          {/* iOS: trainer registration must happen on the web — Apple 3.1.1 */}
          {native && (
            <div
              className="p-4 rounded-xl"
              style={{ backgroundColor: theme.background }}
            >
              <p className="text-sm font-medium mb-1" style={{ color: theme.text }}>
                Als Trainer registrieren
              </p>
              <p className="text-sm" style={{ color: theme.textMuted }}>
                Trainer-Konten werden auf{' '}
                <strong style={{ color: theme.text }}>mojo.sschuldt.de</strong>{' '}
                erstellt. Bitte registrieren Sie sich dort und kehren Sie dann zurück,
                um sich einzuloggen.
              </p>
            </div>
          )}

          {/* First Name */}
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium mb-2"
              style={{ color: theme.text }}
            >
              Vorname
            </label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="input-neu"
              placeholder="Max"
              required
              autoComplete="given-name"
            />
          </div>

          {/* Last Name */}
          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium mb-2"
              style={{ color: theme.text }}
            >
              Nachname
            </label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="input-neu"
              placeholder="Mustermann"
              required
              autoComplete="family-name"
            />
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium mb-2"
              style={{ color: theme.text }}
            >
              Email
            </label>
            <div className="relative">
              <div
                className="absolute left-4 top-1/2 -translate-y-1/2"
                style={{ color: theme.textMuted }}
              >
                <Icons.Envelope size={20} />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-neu pl-12"
                placeholder="deine@email.de"
                required
                autoComplete="email"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium mb-2"
              style={{ color: theme.text }}
            >
              Passwort
            </label>
            <div className="relative">
              <div
                className="absolute left-4 top-1/2 -translate-y-1/2"
                style={{ color: theme.textMuted }}
              >
                <Icons.Lock size={20} />
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-neu pl-12 pr-12"
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2"
                style={{ color: theme.textMuted }}
                tabIndex={-1}
              >
                {showPassword ? <Icons.Hide size={20} /> : <Icons.View size={20} />}
              </button>
            </div>
          </div>

          {/* Password confirmation */}
          <div>
            <label
              htmlFor="passwordConfirm"
              className="block text-sm font-medium mb-2"
              style={{ color: theme.text }}
            >
              Passwort wiederholen
            </label>
            <div className="relative">
              <div
                className="absolute left-4 top-1/2 -translate-y-1/2"
                style={{ color: theme.textMuted }}
              >
                <Icons.Lock size={20} />
              </div>
              <input
                id="passwordConfirm"
                data-testid="register-password-confirm"
                type={showPasswordConfirm ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="input-neu pl-12 pr-12"
                placeholder="••••••••"
                required
                autoComplete="new-password"
                aria-invalid={passwordConfirm.length > 0 && passwordConfirm !== password}
              />
              <button
                type="button"
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                className="absolute right-4 top-1/2 -translate-y-1/2"
                style={{ color: theme.textMuted }}
                tabIndex={-1}
                aria-label={showPasswordConfirm ? 'Passwort verbergen' : 'Passwort anzeigen'}
              >
                {showPasswordConfirm ? <Icons.Hide size={20} /> : <Icons.View size={20} />}
              </button>
            </div>
            {passwordConfirm.length > 0 && passwordConfirm !== password && (
              <p className="text-xs mt-1" style={{ color: theme.error }}>
                Die Passwörter stimmen nicht überein.
              </p>
            )}
          </div>

          {/* Dog Name (CUSTOMER only) */}
          {role === 'CUSTOMER' && (
            <div>
              <label
                htmlFor="dogName"
                className="block text-sm font-medium mb-2"
                style={{ color: theme.text }}
              >
                Name deines Hundes
              </label>
              <input
                id="dogName"
                type="text"
                value={dogName}
                onChange={(e) => setDogName(e.target.value)}
                className="input-neu"
                placeholder="Bello"
                required
                autoComplete="off"
              />
            </div>
          )}

          {/* Optional contact / intake fields (CUSTOMER only) */}
          {role === 'CUSTOMER' && (
            <>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.text }}
                >
                  Telefon <span style={{ color: theme.textMuted }}>(optional)</span>
                </label>
                <input
                  id="phone"
                  data-testid="register-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="input-neu"
                  placeholder="+49 170 1234567"
                  maxLength={50}
                  autoComplete="tel"
                />
              </div>

              <div>
                <label
                  htmlFor="address"
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.text }}
                >
                  Adresse <span style={{ color: theme.textMuted }}>(optional)</span>
                </label>
                <input
                  id="address"
                  data-testid="register-address"
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="input-neu"
                  placeholder="Straße, PLZ Ort"
                  maxLength={500}
                  autoComplete="street-address"
                />
              </div>

              <div>
                <label
                  htmlFor="acquisitionSource"
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.text }}
                >
                  Wie hast du von uns erfahren? <span style={{ color: theme.textMuted }}>(optional)</span>
                </label>
                <select
                  id="acquisitionSource"
                  data-testid="register-acquisition-source"
                  value={acquisitionSource}
                  onChange={(e) => setAcquisitionSource(e.target.value as typeof acquisitionSource)}
                  className="input-neu"
                >
                  <option value="">Bitte wählen</option>
                  <option value="referral">Empfehlung</option>
                  <option value="web">Internet</option>
                  <option value="other">Sonstiges</option>
                </select>
              </div>
            </>
          )}

          {/* Optional dog profile (CUSTOMER only) — MOJ-31. Everything here is optional
              and can also be filled in later on the profile page. */}
          {role === 'CUSTOMER' && (
            <Collapsible title="Mehr über deinen Hund (optional)">
              <div className="space-y-4" data-testid="register-dog-profile">
                <p className="text-xs" style={{ color: theme.textMuted }}>
                  Diese Angaben helfen deinem Trainer, das Training anzupassen. Du kannst sie
                  auch später in deinem Profil ergänzen.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="dogBreed" className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                      Rasse
                    </label>
                    <input
                      id="dogBreed"
                      type="text"
                      value={dogBreed}
                      onChange={(e) => setDogBreed(e.target.value)}
                      className="input-neu"
                      placeholder="z.B. Labrador"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label htmlFor="dogBirthDate" className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                      Geburtsdatum
                    </label>
                    <input
                      id="dogBirthDate"
                      type="date"
                      value={dogBirthDate}
                      onChange={(e) => setDogBirthDate(e.target.value)}
                      className="input-neu"
                    />
                  </div>
                  <div>
                    <label htmlFor="dogGender" className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                      Geschlecht
                    </label>
                    <select
                      id="dogGender"
                      value={dogGender}
                      onChange={(e) => setDogGender(e.target.value)}
                      className="input-neu"
                    >
                      <option value="">Wählen</option>
                      {DOG_GENDER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="dogOwnership" className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                      Halter
                    </label>
                    <select
                      id="dogOwnership"
                      value={dogOwnership}
                      onChange={(e) => setDogOwnership(e.target.value)}
                      className="input-neu"
                    >
                      <option value="">Wählen</option>
                      {DOG_OWNERSHIP_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="trainingSince" className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                      Im Training seit
                    </label>
                    <input
                      id="trainingSince"
                      type="date"
                      value={trainingSince}
                      onChange={(e) => setTrainingSince(e.target.value)}
                      className="input-neu"
                    />
                  </div>
                  <div>
                    <label htmlFor="vaccinationStatus" className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                      Impfstatus
                    </label>
                    <select
                      id="vaccinationStatus"
                      value={vaccinationStatus}
                      onChange={(e) => setVaccinationStatus(e.target.value)}
                      className="input-neu"
                    >
                      <option value="">Wählen</option>
                      {VACCINATION_STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="isFirstDog" className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                      Erster Hund?
                    </label>
                    <select
                      id="isFirstDog"
                      value={isFirstDog}
                      onChange={(e) => setIsFirstDog(e.target.value)}
                      className="input-neu"
                    >
                      <option value="">Wählen</option>
                      {FIRST_DOG_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="livingSituation" className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                      Wohnsituation
                    </label>
                    <select
                      id="livingSituation"
                      value={livingSituation}
                      onChange={(e) => setLivingSituation(e.target.value)}
                      className="input-neu"
                    >
                      <option value="">Wählen</option>
                      {LIVING_SITUATION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="city" className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                      Stadt
                    </label>
                    <input
                      id="city"
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="input-neu"
                      placeholder="Hamburg"
                      maxLength={100}
                      autoComplete="address-level2"
                    />
                  </div>
                  <div>
                    <label htmlFor="country" className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                      Land
                    </label>
                    <input
                      id="country"
                      type="text"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="input-neu"
                      placeholder="Deutschland"
                      maxLength={100}
                      autoComplete="country-name"
                    />
                  </div>
                </div>
              </div>
            </Collapsible>
          )}

          {/* GDPR Consent */}
          <div className="flex items-start gap-3">
            <input
              id="gdprConsent"
              type="checkbox"
              checked={gdprConsent}
              onChange={(e) => setGdprConsent(e.target.checked)}
              className="mt-1 h-4 w-4 rounded"
              style={{ accentColor: theme.primary }}
            />
            <label htmlFor="gdprConsent" className="text-sm leading-relaxed" style={{ color: theme.textMuted }}>
              Ich habe die{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: theme.primary }}>
                Datenschutzerklärung
              </a>{' '}
              und die{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: theme.primary }}>
                Nutzungsbedingungen
              </a>{' '}
              gelesen und stimme ihnen zu. *
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div
              role="alert"
              className="p-4 rounded-xl flex items-center gap-3"
              style={{
                backgroundColor: `${theme.error}15`,
                color: theme.error,
              }}
            >
              <Icons.Warning size={20} weight="fill" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3.5"
          >
            {isLoading ? (
              <>
                <Spinner size="sm" />
                <span>Konto erstellen...</span>
              </>
            ) : (
              <span>Konto erstellen</span>
            )}
          </button>
        </form>

        {/* Login Link */}
        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm"
            style={{ color: theme.textMuted }}
          >
            Bereits registriert? Anmelden
          </Link>
        </div>
      </div>
    </div>
  );
}
